/**
 * AppToolbar — toolbar superior do layout autenticado.
 *
 * Exibe a identidade da aplicação à esquerda e, à direita,
 * ações globais (tema e notificações como placeholders) seguidas
 * do menu de contexto do usuário logado (Avatar + nome + logout).
 *
 * ─────────────────────────────────────────────────────────────────
 * COMO USAR
 * ─────────────────────────────────────────────────────────────────
 * Renderize acima do <main> na área de conteúdo autenticada,
 * passando o objeto `auth` lido do localStorage.
 * ─────────────────────────────────────────────────────────────────
 *
 * @example
 * <AppToolbar auth={{ nome: 'João Silva', token: 'abc' }} />
 *
 * @prop {object}   auth                   Usuário logado — deve conter `nome` e `token`
 * @prop {function} navigate               Navegação SPA (ganchos/withRouter) - usada pelos itens
 *                                          do menu de contexto que levam a outra rota (Utilização,
 *                                          Configurações), evitando reload de página inteira.
 * @prop {function} onToggleSidebar        Callback chamado ao clicar no botão sanduíche
 * @prop {function} onToggleConfigurator   Callback chamado ao clicar no botão de tema
 */
import React, { Component, createRef } from 'react';

import { Toolbar }        from 'primereact/toolbar';
import { Button }         from 'primereact/button';
import { Avatar }         from 'primereact/avatar';
import { Menu }           from 'primereact/menu';
import { Dialog }         from 'primereact/dialog';
import { confirmDialog }  from 'primereact/confirmdialog';

import { UtilService } from 'hay-core-react';
import IconeHay    from '../../images/logo-loading.png';

class AppToolbar extends Component {

    constructor(props) {
        super(props);
        this.menuRef = createRef();
        this.state = { releaseNotesVisivel: false };
    }

    sair() {
        UtilService.efetuarLogout();
    }

    abrirReleaseNotes = () => {
        if (!this.props.auth?.urlReleaseNotes) return;
        this.setState({ releaseNotesVisivel: true });
    }

    redefinirInterface(t) {
        confirmDialog({
            header:          t('main.toolbar.redefinirInterface.confirmacao.header'),
            message:         t('main.toolbar.redefinirInterface.confirmacao.mensagem'),
            icon:            'pi pi-refresh',
            defaultFocus:    'reject',
            acceptClassName: 'p-button-warning',
            draggable:       false,
            className:       'w-3',
            accept: () => {
                const auth = UtilService.getLocalStorage('auth');
                localStorage.clear();
                UtilService.saveLocalStorage('auth', auth);
                UtilService.showToast({
                    severity: 'success',
                    summary:  t('main.toolbar.redefinirInterface.sucesso'),
                    detail:   t('main.toolbar.redefinirInterface.sucesso.detalhe'),
                });
            },
        });
    }

    render() {
        const { auth, navigate, onToggleSidebar, onToggleConfigurator } = this.props;
        const t = (chave) => UtilService.traduzir(chave);

        const userMenuItems = [
            {
                template: () => (
                    <div className="px-3 py-2" style={{ overflow: 'hidden' }}>
                        <div className="flex align-items-center gap-2">
                            <Avatar icon="pi pi-user" shape="circle" style={{ flexShrink: 0 }} />
                            <span className="font-bold" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{auth.nome}</span>
                        </div>
                        {auth.grupoNome && (
                            <div className="flex align-items-center gap-2 mt-1 text-color-secondary text-sm" style={{ paddingLeft: '2.25rem' }}>
                                <i className="pi pi-tags" style={{ flexShrink: 0 }} />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{auth.grupoNome}</span>
                            </div>
                        )}
                        {auth.publicacaoNome && (
                            <div className="flex align-items-center gap-2 mt-1 text-color-secondary text-sm" style={{ paddingLeft: '2.25rem' }}>
                                <i className="pi pi-globe" style={{ flexShrink: 0 }} />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{auth.publicacaoNome}</span>
                            </div>
                        )}
                    </div>
                ),
            },
            { separator: true },
            { label: t('main.minhautilizacao'), icon: 'pi pi-wallet', command: () => { navigate({ pathname: '/minhautilizacao' }); } },
            { label: t('main.toolbar.configuracao'), icon: 'pi pi-cog', command: () => { navigate({ pathname: '/configuracao' }); } },
            { label: t('main.toolbar.redefinirInterface'), icon: 'pi pi-refresh', command: () => this.redefinirInterface(t) },
            { label: t('main.toolbar.releaseNotes'), icon: 'pi pi-book', command: () => this.abrirReleaseNotes() },
            { label: t('main.toolbar.sair'),               icon: 'pi pi-sign-out', command: () => this.sair() },
        ];

        const start = (
            <span className="inline-flex align-items-center gap-2">
                <Button id="menu-principal"
                    icon="pi pi-bars"
                    text
                    severity="secondary"
                    size="small"
                    onClick={onToggleSidebar}
                    tooltip={t('main.toolbar.menu')}
                    tooltipOptions={{ position: 'bottom' }}
                />
                <img src={IconeHay} alt="logo" width="28" height="28" />
                <span className="font-semibold text-xl text-primary">{t('main.sistema.titulo')}</span>
            </span>
        );

        const end = (
            <div className="flex align-items-center gap-1">
                <Button
                    icon="pi pi-palette"
                    label={t('main.toolbar.tema')}
                    text
                    severity="secondary"
                    size="small"
                    onClick={onToggleConfigurator}
                    tooltip={t('main.toolbar.tema.tooltip')}
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                    icon="pi pi-bell"
                    text
                    severity="secondary"
                    size="small"
                    disabled
                    tooltip={t('main.toolbar.embreve')}
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                    text
                    severity="secondary"
                    size="small"
                    className="gap-2"
                    onClick={e => this.menuRef.current.toggle(e)}
                >
                    <Avatar icon="pi pi-user" shape="circle" size="small" />
                    <span className="font-medium">{auth.nome}</span>
                    <i className="pi pi-chevron-down text-xs" />
                </Button>
                <Menu
                    ref={this.menuRef}
                    model={userMenuItems}
                    popup
                    pt={{
                        root: { style: { width: 'auto', maxWidth: '200px' } },
                        menuitem: { style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
                        label: { style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
                    }}
                />
            </div>
        );

        return (
            <>
                <Toolbar
                    start={start}
                    end={end}
                    className="border-noround border-bottom-1 surface-border px-4 py-2"
                    style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                />
                <Dialog
                    header={t('main.toolbar.releaseNotes')}
                    visible={this.state.releaseNotesVisivel}
                    onHide={() => this.setState({ releaseNotesVisivel: false })}
                    style={{ width: '50vw', height: '90vh' }}
                    breakpoints={{ '960px': '90vw' }}
                    contentStyle={{ height: '100%' }}
                    maximizable
                >
                    <iframe
                        title="release-notes"
                        src={auth?.urlReleaseNotes}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                </Dialog>
            </>
        );
    }
}

export default AppToolbar;
